import React, { useCallback, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { GerenciadorPrecos } from '../../../@types/GerenciadorPrecos';
import { Sort } from '../../../@types/sort';
import { SET_GERENCIADOR_CONTEXT_MENU } from '../../../reducers/gerenciadorPrecos/contextMenu';
import {
  selectorDatapoints,
  SET_GERENCIADOR_DATAPOINT_BY_INDEX,
  UPDATE_GERENCIADOR_DATAPOINT_BY_ID,
  UPDATE_GERENCIADOR_DATAPOINT_BY_INDEX,
} from '../../../reducers/gerenciadorPrecos/datapoints';
import { UPDATE_DATAPOINT_CACHE_BY_ID } from '../../../reducers/gerenciadorPrecos/datapointsCache';
import { extractApiErrorMessage } from '../../../utils/extractApiErrorMessage';

import { RootState } from '../../../@types/RootState';
import { AlertWithTimestamp } from '../../../components/AlertWithTimestamp';
import ExceptionSaveDatapoint from '../../../data/ExceptionSaveDatapoint';
import { SET_SHOULD_UPDATE_BIG_NUMBERS } from '../../../reducers/gerenciadorPrecos/bigNumbers/shouldUpdateBigNumbers';
import { selectorOfferDataView } from '../../../reducers/gerenciadorPrecos/offerDataView';
import { useEditedPriceManagerStore } from '../../../store/priceManagerStore';
import { useSelectedDatapoints } from './hooks';
import { useVisibleExpandedRow } from './hooks/useVisibleExpandedRow';
import useGerenciadorSort from './hooks/useGerenciadorSort';
import { saveEditedDatapoint } from './services';
import { getContextMenuRow } from './utils/contextMenuRow';
import { getAutoOfferRowExpandedHeight, getOfferRowExpansionState } from './utils/offerRowExpansion';
import * as calcHandlers from './utils/utils';

type RowData = GerenciadorPrecos.RowData;

const ROW_KEY = 'productsToBePricedId';

const useGerenciadorTable = () => {
  const datapointExhibitionType = useSelector((state: RootState) => {
    return state.datapointExhibitionType.datapointExhibitionType;
  });

  const dispatch = useDispatch();

  const datapoints = useSelector(selectorDatapoints);
  const datapointsRef = useRef(datapoints);
  datapointsRef.current = datapoints;

  const { sort, sortPriceType, datakeys, onSortColumn, onUpdateSortColumn, getSelectedSortOptionValue } = useGerenciadorSort();

  const expandedRowKey = useVisibleExpandedRow();

  const expandedRowOptions = useSelector((state: RootState) => {
    return state.gerenciadorPrecosReducer.expandedRowKey.options;
  });
  const { autoExpandRows } = useSelector(selectorOfferDataView);
  const isOfferDataActive = useSelector((state: RootState) => state.gerenciadorPrecosReducer.quickActionsFilters.isOfferData.isActive);

  const [dataKeyProduto, setDataKeyProduto] = React.useState('productId');

  const selectedDatapoints = useSelectedDatapoints();

  const { updateList, handleCheckAll, isToggleChecked } = selectedDatapoints;

  const setUpdateBigNumbers = useCallback((shouldUpdate: boolean) => dispatch(SET_SHOULD_UPDATE_BIG_NUMBERS(shouldUpdate)), [dispatch]);

  const handleContextMenu = React.useCallback(
    (rowData: RowData, event?: React.MouseEvent) => {
      dispatch(SET_GERENCIADOR_CONTEXT_MENU({ rowData, row: getContextMenuRow(event) }));
    },
    [dispatch],
  );
  const handleUpdateCampo = React.useCallback(
    (name: GerenciadorPrecos.Inputs, value: number, rowData: RowData, index: number) => {
      if (!rowData?.productsToBePricedId || useEditedPriceManagerStore.getState().isPendingEditedPriceRow(rowData.productsToBePricedId)) {
        return;
      }

      let data: Partial<RowData> = {
        priceStatus: 'EDITED',
      };

      const isValidValue = value != null && !isNaN(Number(value));
      const numericValue = Number(value);

      if (isValidValue && (numericValue > 0 || name === 'novaMargem' || name === 'novoPreco')) {
        data = { ...data, novoCalculo: true };
        switch (name) {
          case 'novoPreco':
            data = {
              ...data,
              ...calcHandlers.handleCalculoPreco(numericValue, rowData),
            };
            break;
          case 'novaCompetitividade':
            data = {
              ...data,
              ...calcHandlers.handleCalculoCompetitividade(numericValue, rowData),
            };
            break;
          case 'novaMargem':
            data = {
              ...data,
              ...calcHandlers.handleCalculoMargem(numericValue, rowData),
            };
            break;
          default:
            break;
        }
      } else if (name === 'novoPreco' && isValidValue) {
        data = {
          ...data,
          price: numericValue,
          novaMargem: 0,
          novaCompetitividade: 0,
          novoCalculo: true,
        };
      } else {
        data = {
          ...data,
          price: isValidValue ? numericValue : 0,
          novaMargem: 0,
          novaCompetitividade: 0,
        };
      }

      dispatch(UPDATE_GERENCIADOR_DATAPOINT_BY_INDEX({ index, data }));
    },
    [dispatch],
  );

  const handleRestoreRow = React.useCallback(
    (rowData: RowData, index: number) => {
      dispatch(SET_GERENCIADOR_DATAPOINT_BY_INDEX({ index, rowData }));
    },
    [dispatch],
  );

  const handleBlurSuccess = useCallback(
    (editedDatapoints: Partial<RowData>[]) => {
      if (!editedDatapoints.length) return;

      editedDatapoints.forEach(
        ({
          productsToBePricedId = '',
          price = null,
          priceStatus = null,
          newMargin: novaMargem = null,
          newCompetitivenessPrice: novaCompetitividade = null,
          wholesale = null,
          offerDetails,
          shouldUpdateProduct = false,
        }) => {
          const currentRowData = datapointsRef.current.find(datapoint => {
            return datapoint.productsToBePricedId === productsToBePricedId;
          });

          if (!currentRowData || !productsToBePricedId) return;

          const updateData: Partial<RowData> = {};

          if (shouldUpdateProduct) {
            const hasNewerLocalChange = currentRowData.novoCalculo && currentRowData.price !== null && currentRowData.price !== price;

            if (!hasNewerLocalChange) {
              updateData.price = price;
              updateData.novaMargem = novaMargem;
              updateData.novaCompetitividade = novaCompetitividade;

              // Sem status na resposta, mantém o status local: a linha foi editada e o
              // destaque de quebra de limite depende desse marcador
              if (priceStatus) updateData.priceStatus = priceStatus;
            }

            updateData.novoCalculo = false;
          }

          if (wholesale) {
            updateData.wholesale = {
              ...wholesale,
              ...currentRowData.wholesale,
              price: wholesale.price,
              newMargin: wholesale.newMargin,
              newCompetitivenessPrice: wholesale.newCompetitivenessPrice,
              discountPercentage: wholesale.discountPercentage,
            };
          }

          if (offerDetails !== undefined) {
            updateData.offerDetails = offerDetails;
          }

          if (Object.keys(updateData).length > 0 && datapointExhibitionType !== 'FAMILY') {
            dispatch(
              UPDATE_GERENCIADOR_DATAPOINT_BY_ID({
                id: productsToBePricedId,
                data: updateData,
              }),
            );
          }
        },
      );
    },
    [datapointExhibitionType, dispatch],
  );

  const handleBlur = React.useCallback(
    async (rowData: RowData, index: number) => {
      // Guard against null/undefined rowData (can happen when pagination changes during edit)
      if (!rowData || !rowData.productsToBePricedId) {
        console.warn('handleBlur skipped: rowData or productsToBePricedId is null/undefined', {
          rowData,
          index,
        });
        return;
      }

      const { novoCalculo, storeId, productsToBePricedId, productId, price, storeName, clusterId } = rowData;

      if (!novoCalculo) return;

      const { isPendingEditedPriceRow, startPendingEditedPriceRow, finishPendingEditedPriceRow } = useEditedPriceManagerStore.getState();
      if (isPendingEditedPriceRow(productsToBePricedId)) {
        return;
      }

      startPendingEditedPriceRow(productsToBePricedId);

      const isSelected = isToggleChecked(productsToBePricedId);

      if (isSelected) {
        dispatch(
          UPDATE_DATAPOINT_CACHE_BY_ID({
            productsToBePricedId,
            updateList,
            rowData,
          }),
        );
      }

      const originalRowData = datapoints.find(datapoint => datapoint.productsToBePricedId === productsToBePricedId);

      try {
        if (price == null || (typeof price !== 'number' && isNaN(Number(price)))) {
          console.warn('Invalid price value:', price);
          dispatch(
            UPDATE_GERENCIADOR_DATAPOINT_BY_ID({
              id: productsToBePricedId,
              data: {
                ...(originalRowData ?? {}),
                novoCalculo: false,
              },
            }),
          );
          return;
        }

        const { data = [], status } = await saveEditedDatapoint({
          productId,
          storeId,
          price: Number(price),
          productsToBePricedId,
          datapointExhibitionType,
          clusterId,
        });

        if (status !== 200) {
          // Em caso de erro, apenas remove a flag novoCalculo mas preserva o valor editado
          dispatch(
            UPDATE_GERENCIADOR_DATAPOINT_BY_ID({
              id: productsToBePricedId,
              data: {
                novoCalculo: false,
                // Preserva o preço editado pelo usuário
              },
            }),
          );

          const datapoint = { productId, storeId, storeName };
          const { message } = new ExceptionSaveDatapoint(datapoint);
          AlertWithTimestamp({ message });
          return;
        }

        setUpdateBigNumbers(true);

        handleBlurSuccess(data);
      } catch (error) {
        console.error('Error saving price:', error);
        dispatch(
          UPDATE_GERENCIADOR_DATAPOINT_BY_ID({
            id: productsToBePricedId,
            data: {
              ...(originalRowData ?? {}),
              novoCalculo: false,
            },
          }),
        );

        const errorMessage = extractApiErrorMessage(error);

        if (!errorMessage) {
          const datapoint = { productId, storeId, storeName };
          const { message } = new ExceptionSaveDatapoint(datapoint);
          AlertWithTimestamp({ message });
        } else {
          AlertWithTimestamp({ message: errorMessage });
        }
      } finally {
        finishPendingEditedPriceRow(productsToBePricedId);
      }
    },
    [isToggleChecked, datapoints, dispatch, updateList, datapointExhibitionType, setUpdateBigNumbers, handleBlurSuccess],
  );

  const sortModel: Sort = React.useMemo(
    () => ({
      orderBy: sort.orderBy,
      type: sort.type,
    }),
    [sort],
  );

  const onChangeCheckAll = React.useCallback((checked: boolean) => handleCheckAll(checked, datapoints), [handleCheckAll, datapoints]);

  const { expandedRowKeys, isAutoExpandActive, rowExpandedHeight } = useMemo(
    () =>
      getOfferRowExpansionState({
        datapoints,
        expandedRowKey,
        isOfferDataActive,
        autoExpandRows,
      }),
    [autoExpandRows, datapoints, expandedRowKey, isOfferDataActive],
  );

  const rowHeight = useMemo(() => {
    if (!isAutoExpandActive) return 64;

    return (rowData?: RowData) => {
      if (!rowData?.offerDetails) return 64;
      return 64 + getAutoOfferRowExpandedHeight(rowData, expandedRowKey);
    };
  }, [expandedRowKey, isAutoExpandActive]);

  return {
    data: datapoints,
    datakeys,
    rowKey: ROW_KEY,
    sortColumn: sort.type,
    sortType: sort.orderBy,
    sortPriceType,
    headerHeight: 46,
    rowHeight,
    minHeight: 600,
    rowExpandedHeight,
    dataKeyProduto,
    sort: sortModel,
    expandedRowKeys,
    expandedRowOptions: expandedRowOptions,
    selectedDatapoints,
    onSortColumn,
    onUpdateSortColumn,
    getSelectedSortOptionValue,
    handleContextMenu,
    handleBlur,
    handleRestoreRow,
    handleUpdateCampo,
    onChangeCheckAll,
    setDataKeyProduto,
  } as const;
};

export default useGerenciadorTable;
